import { BookingFlowProduct } from '@/app/book/stores/useCartStore'
import { ShipmentItem } from './PaymentOrderSummary'

const shipmentFieldsToSum = [
  'retail_ship_rate',
  'paid_insurance_cents',
  'handling_fee_cents',
  'fuel_surcharge_fee_cents',
  'discount_cents',
  'wholesale_discount_cents',
  'clearance_fee_cents',
  'total_price_cents',
] as const

// prettier-ignore
type ShippingFields = typeof shipmentFieldsToSum[number]
type ShippingFieldsSum = Record<ShippingFields, number>

export function getShippingFieldsSum(
  shipments: OrderShipment[],
  toExclude: ShippingFields[] = [],
) {
  return shipments.reduce<ShippingFieldsSum>(
    (acc, shipment) => {
      shipmentFieldsToSum.forEach((field) => {
        if (field !== 'total_price_cents' && !toExclude.includes(field)) {
          acc[field] += shipment[field]
        }

        if (
          [
            'paid_insurance_cents',
            'handling_fee_cents',
            'fuel_surcharge_fee_cents',
            'clearance_fee_cents',
            'retail_ship_rate',
            'wholesale_discount_cents',
          ].includes(field)
        ) {
          if (!toExclude.includes(field))
            acc.total_price_cents += shipment[field]
        }
      })
      return acc
    },
    {
      retail_ship_rate: 0,
      paid_insurance_cents: 0,
      handling_fee_cents: 0,
      fuel_surcharge_fee_cents: 0,
      clearance_fee_cents: 0,
      discount_cents: 0,
      wholesale_discount_cents: 0,
      total_price_cents: 0,
    },
  )
}

type AdminFields = ({ [key: string]: number } | number | undefined)[]

export function getAdminFieldsSum(fields: AdminFields) {
  return fields.reduce<ShippingFieldsSum>(
    (acc, field) => {
      if (typeof field === 'object') {
        acc.total_price_cents += Object.values(field).reduce(
          (sum, current) => sum + current,
          0,
        )
      } else {
        acc.total_price_cents += field ?? 0
      }
      return acc
    },
    {
      retail_ship_rate: 0,
      paid_insurance_cents: 0,
      handling_fee_cents: 0,
      fuel_surcharge_fee_cents: 0,
      clearance_fee_cents: 0,
      discount_cents: 0,
      wholesale_discount_cents: 0,
      total_price_cents: 0,
    },
  )
}

export function getShipments(order?: Partial<Order>) {
  const shipments: OrderShipment[] = []

  order?.legs?.forEach((leg) => {
    if (leg.travelers?.length) {
      leg.travelers.forEach((traveler) => {
        shipments.push(...traveler.shipments)
      })
    }
  })

  return shipments
}

export function travelerByTrackingId(
  trackingId: string,
  order?: Partial<Order>,
): OrderLegTraveler | undefined {
  if (!order || !order.legs) return undefined

  // Flatten travelers from all legs
  const allTravelers = order.legs.flatMap((leg) => leg.travelers || [])

  return allTravelers.find((traveler) =>
    traveler.shipments?.some((shipment) => shipment.tracking_id === trackingId),
  )
}

export function computeTotalPrice(
  totalPriceCents: number = 0,
  discountRate: number = 0,
) {
  const totalPriceWhole = totalPriceCents / 100

  const discountRateCents = discountRate
    ? Math.floor(totalPriceCents * (discountRate / 100))
    : 0

  // TBD: if `discountRate` will be validated here (0..100) or if we will
  // calculate it by `discount_cents` field
  const totalPrice = discountRate
    ? (totalPriceCents - discountRateCents) / 100
    : totalPriceWhole

  return { totalPrice, discountRateCents }
}

export function groupByTraveler(
  shipments: ShipmentItem[],
  selectedProducts: BookingFlowProduct[],
): Record<string, ShipmentItem[]> {
  const productTravelers = selectedProducts?.map((item) => {
    return {
      id: item.id,
      product: item,
      matched: false,
    }
  })
  // you can get two of the same product for different travelers
  return shipments.reduce<Record<string, ShipmentItem[]>>((acc, item) => {
    const productId = item.rate.id
    const matched = productTravelers?.find((product) => {
      const candidate = product.id === productId && !product.matched
      if (candidate) {
        product.matched = true
      }
      return candidate
    })
    const travelerName = (matched?.product?.traveler ||
      item.traveler?.label ||
      '') as string
    if (!acc[travelerName]) {
      acc[travelerName] = []
    }
    acc[travelerName].push(item)
    return acc
  }, {})
}

export function groupAndSortByProductLine(
  arr: ShipmentItem[],
  productLineCounter: Record<string, number>,
) {
  const groupByProductLine = arr.reduce<Record<string, ShipmentItem[]>>(
    (acc, item) => {
      const [productLine] = item.rate.label.split(',')

      if (!acc[productLine]) {
        acc[productLine] = []
      }

      acc[productLine].push(item)

      return acc
    },
    {},
  )

  const sortedShipments: ShipmentItem[] = []

  Object.values(groupByProductLine).forEach((shipments) => {
    const shipmentsWithNthType = shipments.map((item) => {
      const [productLine, displayName] = item.rate.label.split(',')

      // Increment the product line counter for the current product line
      if (!productLineCounter[productLine]) {
        productLineCounter[productLine] = 0
      }
      productLineCounter[productLine]++
      return {
        ...item,
        rate: {
          ...item.rate,
          label: `${productLine} #${productLineCounter[productLine]} (${displayName})`,
        },
      }
    })

    sortedShipments.push(...shipmentsWithNthType)
  })

  return sortedShipments
}

export function getProductLinesCount(products: OrderProductLabel[]) {
  return products.reduce<Record<string, number>>((acc, product) => {
    const productLine = product.product_line.display_name

    return {
      ...acc,
      [productLine]: acc[productLine] ? acc[productLine] + 1 : 1,
    }
  }, {})
}
